import { t } from 'i18next';

import { AlertMessage } from '../../constants/alert-message';
import { ApiErrorResponse } from '../../types/api';
import { CalculatorResponseType, CalculatorType, PostCalcQueryType } from '../../types/calculator';
import { ApiEndpoints } from '../constants/api';
import { ApiGroupNames, EndpointQueryNames } from '../constants/api-group-names';
import { EndpointNames } from '../constants/endpoint-names';
import { requestProcessing } from '../helpers/request-processing';
import { transformErrorWithAlert } from '../helpers/transform-error-with-alert';
import {
  setAppLoader,
  setFromMain,
  setRequestStatus,
  setResultInfo,
  setShowPartners,
} from '../modules/app';
import { Paths } from '../../routes/paths-enum';
import { ResultType, ResultTypeVariants } from '../../constants/result';
import { ErrorCode } from '../../constants/error-code';
import { HttpStatus } from '../../constants/http-status';
import { history, store } from '../configure-store';
import { LOCAL_STORAGE } from '../../constants/local-storage';
import { RANDOM_DEEP_LINK } from '../../utils/random-deep-link';
import { RequestStatus } from '../../types/general';
import { GtmEventEnum, sendGtmEvent } from '../../utils/send-gtm-event';

import { apiSlice } from '.';

export const calculatorApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // getMain: builder.query<GetMainResponseType, void>({
    //   query: () => ({
    //     url: ApiEndpoints.MAIN,
    //     endpoint: EndpointQueryNames.HISTORY_OPERATOR,
    //     // endpoint: EndpointQueryNames.LOCAL_MOCK,
    //     method: 'GET',
    //   }),
    //   transformResponse: (response: GetMainResponseType): GetMainResponseType => {
    //     const { showPartners } = response;
    //
    //     store.dispatch(setShowPartners(showPartners));
    //
    //     if (showPartners) {
    //       history.push(Paths.PARTNERS_PAGE);
    //       sendGtmEvent(GtmEventEnum.mainScreen_partnersListShown);
    //       store.dispatch(setRequestStatus(RequestStatus.MORATORIUM));
    //       store.dispatch(setFromMain(true));
    //     }
    //
    //     return response;
    //   },
    // }),

    getCalculator: builder.query<CalculatorType, void>({
      providesTags: ['ResetCalculator'],
      query: () => {
        const refCodeValue = localStorage.getItem(LOCAL_STORAGE.refCode) || RANDOM_DEEP_LINK;

        if (!localStorage.getItem(LOCAL_STORAGE.refCode)) {
          localStorage.setItem(LOCAL_STORAGE.refCode, refCodeValue);
        }

        return {
          url: ApiEndpoints.CALCULATOR,
          endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
          method: 'GET',
          apiGroupName: ApiGroupNames.LOAN,
          name: EndpointNames.CALCULATOR,
          params: { refCode: refCodeValue },
        };
      },
      transformResponse: (response: CalculatorType): any => {
        const isAuth = sessionStorage.getItem(LOCAL_STORAGE.isAuthorized);

        if (response?.showPartners && isAuth) {
          store.dispatch(setShowPartners(response?.showPartners));
          store.dispatch(setRequestStatus(RequestStatus.MORATORIUM));
          store.dispatch(setFromMain(true));
          sendGtmEvent(GtmEventEnum.mainScreen_partnersListShown);

          return history.push(Paths.PARTNERS_PAGE);
        }
        if (response.loan === null) {
          store.dispatch(
            setResultInfo({
              status: ResultType.info,
              title: t('result.oops'),
              description: t('result.notCalculatorDate'),
              email: true,
              buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.buttonRetry') }],
            }),
          );
          history.push(Paths.RESULT_PAGE);
        } else {
          const processAmounts = (obj: any): any => {
            if (obj && typeof obj === 'object') {
              return Object.fromEntries(
                Object.entries(obj).map(([key, value]) => {
                  if (
                    (key === 'amount' || key === 'minAmount' || key === 'maxAmount') &&
                    typeof value === 'number'
                  ) {
                    return [key, value / 100];
                  }
                  if (typeof value === 'object') {
                    return [key, processAmounts(value)];
                  }

                  return [key, value];
                }),
              );
            }

            return obj;
          };

          return {
            ...response,
            default: response.default ? processAmounts(response.default) : undefined,
            loan: Array.isArray(response.loan) ? response.loan.map(processAmounts) : response.loan,
          };
        }
      },
      transformErrorResponse: (response: ApiErrorResponse) =>
        transformErrorWithAlert({
          response,
          message: AlertMessage.ERROR_OTHER,
        }),
    }),

    postCalculator: builder.mutation<CalculatorResponseType, PostCalcQueryType>({
      query: (data) => ({
        url: ApiEndpoints.PARAMETERS,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'POST',
        apiGroupName: ApiGroupNames.LOAN,
        name: EndpointNames.CALCULATOR,
        body: data,
      }),

      onQueryStarted(args, { dispatch }) {
        dispatch(setAppLoader(true));
      },

      transformErrorResponse: (response: ApiErrorResponse) => {
        const { data, status } = response;

        store.dispatch(setAppLoader(false));

        const errorHandlers = {
          [ErrorCode.TIMEOUT]: () => {
            store.dispatch(
              setResultInfo({
                type: ResultTypeVariants.processingProblem,
                status: ResultType.info,
                title: t('result.processingRequestTakeSomeTime'),
                description: t('result.operationWaitDescription'),
                buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.ok') }],
              }),
            );
            history.push(Paths.RESULT_PAGE);
          },
          [ErrorCode.UNDEFINED_NEXT_STEP]: () => {
            store.dispatch(
              setResultInfo({
                type: ResultTypeVariants.processingProblem,
                status: ResultType.error,
                title: t('result.applicationInProcessing'),
                description: t('result.pleaseWaitSituationDoesNotChange'),
                buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.ok') }],
              }),
            );
            history.push(Paths.RESULT_PAGE);
          },
          [ErrorCode.ERROR_CREATING_APPLICATION]: () => {
            store.dispatch(
              setResultInfo({
                type: ResultTypeVariants.processingProblem,
                status: ResultType.error,
                title: t('result.failedCreateApplication'),
                description: '',
                buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.ok') }],
              }),
            );
            history.push(Paths.RESULT_PAGE);
          },
          [ErrorCode.DUPLICATE_REQUEST_CREATION]: () => {
            store.dispatch(
              setResultInfo({
                type: ResultTypeVariants.processingProblem,
                status: ResultType.error,
                title: t('result.applicationNotCreated'),
                description: t('result.toCreateNewApplicationCancelPrevious'),
                buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.ok') }],
              }),
            );
            history.push(Paths.RESULT_PAGE);
          },
        };

        if (status === HttpStatus.BAD_REQUEST) {
          return transformErrorWithAlert({ response, message: AlertMessage.BAD_REQUEST });
        }

        if (
          status === HttpStatus.TEMPORARY_PROBLEMS &&
          data?.code === ErrorCode.TEMPORARY_PROBLEMS
        ) {
          return transformErrorWithAlert({ response, message: AlertMessage.TEMPORARY_PROBLEMS });
        }

        if (status === HttpStatus.REGISTRATION_PROBLEM) {
          const errorHandler = errorHandlers[data?.code || ''];

          if (errorHandler) {
            return errorHandler();
          }
        }

        transformErrorWithAlert({
          response,
          message: AlertMessage.ERROR_OTHER,
        });
      },
      transformResponse: (response: any): any => {
        const { nextStep, applicationId } = response;

        requestProcessing({ nextStep, applicationId });
        setTimeout(() => store.dispatch(setAppLoader(false)), 1000);

        return {
          ...response,
        };
      },
    }),
  }),
});

export const { useGetCalculatorQuery, usePostCalculatorMutation } = calculatorApiSlice;
